/**
 * Promoted products event tracking and request context functionality provided by Gopuff Ads
 *
 * To highlight promoted products for debugging, enter this is the dev console:
 *
 * Cookies.set('debug-promoted', 'true')
 *
 * or add query param to request: &ppp-debug=true
 */
class PromotedProductsIntegration {

  static #debugLog(message, obj = '') {
    if (Cookies.get('debug-promoted') === 'true') {
      console.log(`PromotedProductsIntegration | ${message}`, obj)
    }
  }

  static #firePixel (url) {
    PromotedProductsIntegration.#debugLog("Pixel fired for: ", url);
    try {
      fetch(url, { mode: "no-cors"})
    } catch (ignored) {}
  }

  /**
   * IAB impression tracking, 50% visible for 1 second
   */
  static #observeForTrackingImpression(element, impressionUrl, threshold = 0.5) {
    let isVisible = false
    let timer = null
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(entry => {
          if (entry.intersectionRatio >= threshold) {
            if (!isVisible) {
              isVisible = true;
              timer = setTimeout(() => {
                PromotedProductsIntegration.#firePixel(impressionUrl)
                // Stop observing after first trigger
                obs.unobserve(element);
              }, 1000);
            }
          } else {
            isVisible = false;
            clearTimeout(timer);
          }
        });
      },
      { threshold } // Trigger when the given percentage of the element is visible
    );
    observer.observe(element);
  }


  static #attachImpressionListeners() {
    PromotedProductsIntegration.#debugLog('attaching impression listeners')
    const elements = document.getElementsByClassName('product-card-wrapper');
    for (const element of elements) {
      const url = element.dataset?.adImpressionUrl  ;
      if (url) {
        PromotedProductsIntegration.#observeForTrackingImpression(element, url, 0.1);
        // clear url to prevent duplicate impressions
        element.dataset.adImpressionUrl = '';

        // Debug highlighting for promoted
        if (Cookies.get('debug-promoted') === 'true') {
          element.classList.add('promoted-product')
        }
      }
    }
  }

  static #handleAdClickEvent(event, element) {
    const wrapper = element.closest('.product-card-wrapper')
    const url = wrapper?.dataset?.adClickUrl
    if (url) {
      PromotedProductsIntegration.#firePixel(url)
      // clear url to revent duplicates clicks
       wrapper.dataset.adClickUrl = ''
    }
  }


  static #attachClickListeners() {
    PromotedProductsIntegration.#debugLog('attaching click listeners')
    // click on tile or add to cart should result in pixel fire
    const elements = [
      ...document.getElementsByClassName('product-card-wrapper'),
      ...document.getElementsByClassName('quantity-pill__quick-add')
    ]
    for (const element of elements) {
      const wrapper = element.closest('.product-card-wrapper')
      const url = wrapper?.dataset?.adClickUrl
      if (url && element.getAttribute('listener') !== 'true') {
        PromotedProductsIntegration.#debugLog('click url found, adding click event listener for', url)
        element.addEventListener('click', (event) => PromotedProductsIntegration.#handleAdClickEvent(event, element), {once: true});
        // mark this element as already having a listener so we don't re-attach another one on scroll
        element.setAttribute('listener', 'true');
      }
    }
  }

  static #generateUUID() {
    let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    return uuid;
  }

  static #getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  static adsEnabled() {
    const adsChanceCoookieName = 'adsChance'
    let adsChanceValue = Number(Cookies.get(adsChanceCoookieName))
    if (Number.isNaN(adsChanceValue)) {
      adsChanceValue = PromotedProductsIntegration.#getRandomInt(0, 100)
      Cookies.set(adsChanceCoookieName, adsChanceValue)
    }
    const adsChanceThreshold = Number(window.gopuffShopify.settings.promotedProductsPercentage);
    return !!(adsChanceThreshold && adsChanceValue <= adsChanceThreshold)
  }

  static getAdSessionId() {
    const sessionCoookieName = 'adSessionId'
    let adSessionId = Cookies.get(sessionCoookieName)
    if (!adSessionId) {
      adSessionId = PromotedProductsIntegration.#generateUUID()
      Cookies.set(sessionCoookieName, adSessionId, { expires: 365 * 2 });
    }
    return adSessionId;
  }

  static getUserId() {
    return window?.gopuffShopify?.customer?.id
  }

  static attachAdEventListeners() {
    PromotedProductsIntegration.#attachImpressionListeners()
    PromotedProductsIntegration.#attachClickListeners()
  }

  static getPageType() {
    if (window.location.pathname === '/') {
      return 'HOME'
    }
    if (window.location.pathname === '/search') {
      return 'SEARCH'
    }
    if (window.location.pathname.startsWith('/products/')) {
      return 'PDP'
    }
    if (window.location.pathname.startsWith('/pages/') || window.location.pathname.startsWith('/collections/')) {
      return 'COLLECTION'
    }
    return 'OTHER'
  }

  static contentRequestId = PromotedProductsIntegration.setContentRequestId()

  static getContentRequestId() {
    return PromotedProductsIntegration.contentRequestId
  }

  static setContentRequestId() {
    const id = '3p-crq-' + PromotedProductsIntegration.#generateUUID()
    this.#debugLog('Changing content request id to ' +  id)
    PromotedProductsIntegration.contentRequestId = id
    return id
  }
}


const debugParam = new URLSearchParams(window.location.search).get('ppp-debug')
if(debugParam === 'true') {
  Cookies.set('debug-promoted', 'true')
} else if (debugParam === 'false') {
  Cookies.set('debug-promoted', 'false')
}

document.body.addEventListener(PRODUCTS_RENDERED_EVENT, (event) => {
  PromotedProductsIntegration.attachAdEventListeners();
});

navigation.addEventListener('navigate', (event) => {
  PromotedProductsIntegration.setContentRequestId();
});
